import type { Point2 } from '../geometry/matrix';

/** 픽셀당 한 변의 표본 수(4×4 = 16점) — 모양 마스크(SHAPE_MASK_SUPERSAMPLE)와 같다. */
const SUPERSAMPLE = 4;

/**
 * 닫힌 다각형(첫 점을 반복하지 않는다)의 width×height 커버리지 마스크 0~255. 마스크 픽셀 (x, y) 는
 * 다각형 좌표 (x + offset.x, y + offset.y) 부터 한 칸이다. 4×4 슈퍼샘플·even-odd 스캔라인으로 세고,
 * applyShapeMask 와 같은 알파 곱하기로 쓴다.
 */
export const rasterizePolygonMask = (
  points: readonly Point2[],
  width: number,
  height: number,
  offset: { readonly x: number; readonly y: number },
): Uint8Array => {
  const n = SUPERSAMPLE;
  const mask = new Uint8Array(width * height);
  const counts = new Uint16Array(width);
  const crossings: number[] = [];
  const columns = width * n;

  for (let row = 0; row < height; row += 1) {
    counts.fill(0);
    for (let j = 0; j < n; j += 1) {
      const sy = row + (j + 0.5) / n + offset.y;
      crossings.length = 0;
      for (let i = 0; i < points.length; i += 1) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        if (a === undefined || b === undefined) continue;
        // 반개구간 규칙 — 꼭짓점을 두 번 세지 않는다.
        if (a.y > sy !== b.y > sy) crossings.push(a.x + ((sy - a.y) * (b.x - a.x)) / (b.y - a.y));
      }
      crossings.sort((p, q) => p - q);
      for (let k = 0; k + 1 < crossings.length; k += 2) {
        // 표본 열 c 의 x 는 (c + 0.5)/n + offset.x — [from, to) 에 드는 열을 센다.
        const from = ((crossings[k] ?? 0) - offset.x) * n - 0.5;
        const to = ((crossings[k + 1] ?? 0) - offset.x) * n - 0.5;
        const first = Math.max(0, Math.ceil(from));
        const last = Math.min(columns, Math.ceil(to));
        for (let c = first; c < last; c += 1) {
          const col = (c / n) | 0;
          counts[col] = (counts[col] ?? 0) + 1;
        }
      }
    }
    const base = row * width;
    for (let col = 0; col < width; col += 1) {
      mask[base + col] = Math.round((255 * (counts[col] ?? 0)) / (n * n));
    }
  }
  return mask;
};
